import { getBearerToken } from "./auth";
import type { JsonSchema } from "./schema-form/types";

export interface TransformSystemPropertiesDto {
  externalKey?: string;
  sourceTime?: string;
  associationKeys?: string[];
  /** Destination property holding the stage. Absent ⇒ the editor shows no stage column. */
  stateField?: string;
  /** Absent/false ⇒ the editor offers no "remove it" action. */
  canArchive?: boolean;
  /** Built-in state → stage defaults, used to pre-fill on first enable. */
  defaultStateStages?: Record<string, string>;
}

export interface TransformDescriptorDto {
  id: string;
  sourceConnectorType: string;
  destConnectorType: string;
  sourceObjectType: string;
  destObjectType: string;
  description: string;
  /** Plain-language name from the registry ("Orders → HubSpot deals"). Null
   *  when a transform declares none; friendly-labels.ts then falls back. */
  label?: string | null;
  hasParamsSchema: boolean;
  paramsJsonSchema: JsonSchema | null;
  systemProperties: TransformSystemPropertiesDto | null;
  /** Properties a field MAPPING may not drop or rename (spec §6.5). */
  lockedKeys: string[];
  /** Properties a WRITE MASK may not name — `lockedKeys` plus the source-time
   *  property. Superset of `lockedKeys`; the two are not interchangeable. */
  unmaskableKeys: string[];
}

/**
 * The connector projection served by `GET /api/connectors`, narrowed to the
 * fields the admin UI reads. `listConnectors` still answers `any[]` — its six
 * other call sites read the row loosely — so cast at the point of use, the way
 * `ConnectionEditor` already does.
 */
export interface ConnectorDescriptorDto {
  connectorType: string;
  label: string;
  capabilities: string[];
  /** Destination properties that are legitimate identity match keys, keyed by
   *  destination object type (spec §6.8). `null` when none are declared. */
  identityFields: Record<string, string[]> | null;
}

/**
 * The identity fields a destination connector declares for one object type —
 * what the record-adoption editor offers as `idField`. Empty when the connector
 * declares none, which is the signal that the destination can't be adopted into.
 */
export function identityFieldsFor(
  connectors: ConnectorDescriptorDto[],
  connectorType: string | undefined,
  destObjectType: string | undefined,
): string[] {
  if (!connectorType || !destObjectType) return [];
  return connectors.find((c) => c.connectorType === connectorType)?.identityFields?.[destObjectType] ?? [];
}

export interface DiscoverItem {
  id: string;
  name: string;
  description?: string;
}

/** A single pipeline run, projected from the `sync_log` table. */
export interface SyncRun {
  id: string;
  pipelineId: string;
  organizationId: string;
  eventId: string;
  externalId: string;
  objectType: string;
  operation: string;
  sourceUpdatedAt: string | null;
  receivedAt: string;
  workflowId: string;
  workflowRunId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  processingTimeMs: number | null;
  status: string;
  skipReason: string | null;
  errorMessage: string | null;
  destExternalId: string | null;
  /** The AWS account that produced this event, and the environment it maps to.
   *  Null on producers that don't stamp an account (DEV-4848). */
  producingAccount: string | null;
  environmentToken: string | null;
  /** When this run was produced by re-running an earlier run, the id of that
   *  originating run; and the 1-based attempt number (1 for the first run). */
  rerunOf?: string | null;
  attempt?: number;
  /** True when a later re-run of this run reached `completed` — i.e. a failure
   *  that was subsequently recovered. Drives the "re-run ✓" badge (DEV-5957). */
  supersededByRerun?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Per-run data lineage from `sync_log_payloads` — what triggered the run, what
 * it became, what the destination looked like, the deliver outcome, and (on
 * failure) which stage threw. */
export interface RunPayloads {
  syncLogId: string;
  inputEvent: unknown;
  transformOutput: unknown;
  lookupSnapshot: unknown;
  deliverResult: unknown;
  failedStage: string | null;
  createdAt: string;
}

/** Per-id outcome of a bulk re-run (mirrors the API's BulkRerunResult).
 *  `accepted` = enqueued to the ingress; `rejected` = ineligible/missing. */
export interface BulkRerunResult {
  id: string;
  status: "accepted" | "rejected";
  reason?: string;
}

/** The six DLQ-backed buses (mirrors the API's DLQ_BUSES). */
export type DlqBus =
  | "webhook-events" | "poll-events" | "stream-events"
  | "internal-events-profile" | "internal-events-orders" | "deliver";

/** A message drained out of a bus's SQS DLQ into the holding table. */
export interface DlqItem {
  id: string;
  bus: DlqBus;
  sqsMessageId: string;
  messageGroupId: string | null;
  body: unknown;
  eventId: string | null;
  pipelineId: string | null;
  organizationId: string | null;
  receiveCount: number | null;
  sqsSentAt: string | null;
  failureReason: string | null;
  status: "pending" | "refired" | "discarded";
  actionBy: string | null;
  actionAt: string | null;
  drainedAt: string;
  createdAt: string;
}

export interface ManagedQueue {
  id: string;
  organizationId: string;
  name: string;
  queueUrl: string;
  queueArn: string;
  region: string;
  fifo: boolean;
  consumerAwsAccountId: string;
  consumerPrincipalArn: string | null;
  status: string;
  createdAt: string;
}

export interface DlqBusSummary {
  bus: DlqBus;
  sqsDepth: number | null;
  pending: number;
  refired: number;
  discarded: number;
}

export interface DlqSyncResult { bus: DlqBus; drained: number; capped: boolean; error?: string; }
export interface DlqActionResult { id: string; ok: boolean; reason?: string; }

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

/** Field-level detail behind a 4xx. Same shape from both error-handler branches. */
export interface ApiIssue {
  path: (string | number)[];
  message: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** Present on validation failures. "Pipeline validation failed" on its own
     *  names nothing the operator can act on; the issues say which property. */
    public issues: ApiIssue[] = [],
  ) {
    super(message);
  }
}

/** `"Pipeline validation failed: writeMask.adoptedUpdate — these properties …"` */
export function describeApiError(err: unknown): string {
  if (!(err instanceof ApiError)) return err instanceof Error ? err.message : String(err);
  if (err.issues.length === 0) return err.message;
  const lines = err.issues.map((i) => (i.path.length > 0 ? `${i.path.join(".")} — ${i.message}` : i.message));
  return `${err.message}: ${lines.join("; ")}`;
}

/**
 * Every call to the admin API goes through here: bearer token attached,
 * `ApiError` thrown for a non-2xx with the server's code and issues intact,
 * and 204 resolved as undefined.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getBearerToken();
  // Only declare `Content-Type: application/json` when we're actually sending
  // a body. Fastify's default JSON parser rejects empty bodies under a JSON
  // content-type, which 400s every POST that has no body (e.g. testConnection,
  // enablePipeline, rotate-secret without a payload).
  const hasBody = init?.body != null;
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error?.code ?? "unknown",
      body?.error?.message ?? res.statusText,
      Array.isArray(body?.error?.issues) ? body.error.issues : [],
    );
  }
  return body as T;
}

export const api = {
  // organizations
  listOrganizations: () => request<{ organizations: any[] }>("/api/organizations"),
  getOrganization: (id: string) => request<{ organization: any }>(`/api/organizations/${id}`),
  createOrganization: (body: any) =>
    request<{ organization: any }>("/api/organizations", { method: "POST", body: JSON.stringify(body) }),
  updateOrganization: (id: string, body: any) =>
    request<{ organization: any }>(`/api/organizations/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteOrganization: (id: string) =>
    request<void>(`/api/organizations/${id}`, { method: "DELETE" }),

  // environments
  listEnvironments: (q?: { organizationId?: string }) => {
    const qs = q?.organizationId ? `?organizationId=${encodeURIComponent(q.organizationId)}` : "";
    return request<{ environments: any[] }>(`/api/environments${qs}`);
  },
  createEnvironment: (body: any) =>
    request<{ environment: any }>("/api/environments", { method: "POST", body: JSON.stringify(body) }),
  updateEnvironment: (id: string, body: any) =>
    request<{ environment: any }>(`/api/environments/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  setEnvironmentDisabled: (id: string, disabled: boolean) =>
    request<{ environment: any }>(`/api/environments/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ disabled }),
    }),
  deleteEnvironment: (id: string) =>
    request<void>(`/api/environments/${id}`, { method: "DELETE" }),

  // managed queues
  listQueues: (organizationId?: string) =>
    request<{ queues: ManagedQueue[] }>(
      `/api/queues${organizationId ? `?organizationId=${organizationId}` : ""}`,
    ),
  createQueue: (body: {
    organizationId: string;
    name: string;
    region?: string;
    fifo?: boolean;
    consumerAwsAccountId: string;
    consumerPrincipalArn?: string;
  }) => request<{ queue: ManagedQueue }>("/api/queues", { method: "POST", body: JSON.stringify(body) }),
  updateQueueAccess: (id: string, body: { consumerAwsAccountId: string; consumerPrincipalArn?: string }) =>
    request<{ queue: ManagedQueue }>(`/api/queues/${id}/access`, { method: "PUT", body: JSON.stringify(body) }),
  deleteQueue: (id: string) =>
    request<void>(`/api/queues/${id}`, { method: "DELETE" }),

  // connector instances
  listConnectorInstances: () => request<{ instances: any[] }>("/api/connector-instances"),
  getConnectorInstance: (id: string) =>
    request<{ instance: any }>(`/api/connector-instances/${id}`),
  createConnectorInstance: (body: any) =>
    request<{ instance: any }>("/api/connector-instances", { method: "POST", body: JSON.stringify(body) }),
  updateConnectorInstance: (id: string, body: any) =>
    request<{ instance: any }>(`/api/connector-instances/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteConnectorInstance: (id: string) =>
    request<void>(`/api/connector-instances/${id}`, { method: "DELETE" }),
  rotateConnectorInstanceSecret: (id: string, secret: unknown) =>
    request<{ name: string }>(`/api/connector-instances/${id}/rotate-secret`, {
      method: "POST",
      body: JSON.stringify({ secret }),
    }),
  hubspotInstallUrl: (organizationId: string, environment?: string) => {
    const qs = `?organizationId=${encodeURIComponent(organizationId)}${
      environment ? `&environment=${encodeURIComponent(environment)}` : ""
    }`;
    return request<{ url: string; expiresAt: string }>(
      `/api/connector-instances/hubspot-install-url${qs}`,
    );
  },
  salesforceInstallUrl: (organizationId: string, environment?: string) => {
    const qs = `?organizationId=${encodeURIComponent(organizationId)}${
      environment ? `&environment=${encodeURIComponent(environment)}` : ""
    }`;
    return request<{ url: string; expiresAt: string }>(
      `/api/connector-instances/salesforce-install-url${qs}`,
    );
  },

  // pipelines
  listPipelines: (organizationId?: string) =>
    request<{ pipelines: any[] }>(
      organizationId
        ? `/api/pipelines?organizationId=${encodeURIComponent(organizationId)}`
        : "/api/pipelines",
    ),
  getPipeline: (id: string) => request<{ pipeline: any }>(`/api/pipelines/${id}`),
  createPipeline: (body: any) =>
    request<{ pipeline: any; warnings?: string[] }>("/api/pipelines", { method: "POST", body: JSON.stringify(body) }),
  updatePipeline: (id: string, body: any) =>
    request<{ pipeline: any; warnings?: string[] }>(`/api/pipelines/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deletePipeline: (id: string) =>
    request<void>(`/api/pipelines/${id}`, { method: "DELETE" }),
  enablePipeline: (id: string) =>
    request<{ pipeline: any }>(`/api/pipelines/${encodeURIComponent(id)}/enable`, { method: "POST" }),
  disablePipeline: (id: string) =>
    request<{ pipeline: any }>(`/api/pipelines/${encodeURIComponent(id)}/disable`, { method: "POST" }),

  // sync log (pipeline runs)
  listSyncLog: (q?: {
    pipelineId?: string;
    organizationId?: string;
    status?: string;
    cursor?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (q?.pipelineId) qs.set("pipelineId", q.pipelineId);
    if (q?.organizationId) qs.set("organizationId", q.organizationId);
    if (q?.status) qs.set("status", q.status);
    if (q?.cursor) qs.set("cursor", q.cursor);
    if (q?.limit) qs.set("limit", String(q.limit));
    const tail = qs.toString();
    return request<{ entries: SyncRun[]; nextCursor: string | null }>(
      `/api/sync-log${tail ? `?${tail}` : ""}`,
    );
  },
  getSyncLogRow: (id: string) => request<{ entry: SyncRun }>(`/api/sync-log/${id}`),
  getRunPayloads: (id: string) =>
    request<{ payloads: RunPayloads | null }>(`/api/sync-log/${encodeURIComponent(id)}/payloads`),
  rerunRun: (id: string) =>
    request<{ syncLogId: string | null }>(`/api/sync-log/${encodeURIComponent(id)}/rerun`, { method: "POST" }),
  /** Bulk re-run: submits many runs at once. Each is enqueued asynchronously,
   *  so the response reports per-id accepted/rejected rather than final status —
   *  the recovered rows surface in the run feed as their workflows complete. */
  rerunBulk: (ids: string[]) =>
    request<{ accepted: number; rejected: number; results: BulkRerunResult[] }>(
      "/api/sync-log/rerun-bulk",
      { method: "POST", body: JSON.stringify({ ids }) },
    ),

  // dead-letter (DLQ) management
  dlqSummary: () => request<{ buses: DlqBusSummary[] }>("/api/dlq/summary"),
  dlqSync: (bus?: DlqBus) =>
    request<{ results: DlqSyncResult[] }>("/api/dlq/sync", {
      method: "POST",
      body: JSON.stringify(bus ? { bus } : {}),
    }),
  dlqList: (q?: { bus?: DlqBus; status?: string; cursor?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (q?.bus) qs.set("bus", q.bus);
    if (q?.status) qs.set("status", q.status);
    if (q?.cursor) qs.set("cursor", q.cursor);
    if (q?.limit) qs.set("limit", String(q.limit));
    const tail = qs.toString();
    return request<{ items: DlqItem[]; nextCursor: string | null }>(`/api/dlq/items${tail ? `?${tail}` : ""}`);
  },
  dlqItem: (id: string) => request<{ item: DlqItem }>(`/api/dlq/items/${encodeURIComponent(id)}`),
  dlqRefire: (ids: string[]) =>
    request<{ results: DlqActionResult[] }>("/api/dlq/refire", { method: "POST", body: JSON.stringify({ ids }) }),
  dlqDiscard: (ids: string[]) =>
    request<{ results: DlqActionResult[] }>("/api/dlq/discard", { method: "POST", body: JSON.stringify({ ids }) }),

  // registry
  listConnectors: () => request<{ connectors: any[] }>("/api/connectors"),
  listTransforms: () => request<{ transforms: TransformDescriptorDto[] }>("/api/transforms"),

  // discover (reference data for picking property names, owner ids, etc.)
  discover: (instanceId: string, kind: string) =>
    request<{ items: DiscoverItem[] }>(
      `/api/connector-instances/${encodeURIComponent(instanceId)}/discover?kind=${encodeURIComponent(kind)}`,
    ),

  // secrets
  listSecrets: () => request<{ secrets: any[] }>("/api/secrets"),
  getSecret: (name: string) =>
    request<{ secret: any }>(`/api/secrets/${encodeURIComponent(name)}`),
  putSecret: (name: string, value: string) =>
    request<{ secret: any }>(`/api/secrets/${encodeURIComponent(name)}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),
  deleteSecret: (name: string) =>
    request<void>(`/api/secrets/${encodeURIComponent(name)}`, { method: "DELETE" }),

  // overview
  getOverview: () =>
    request<{
      pipelines: { active: number; paused: number; total: number };
      runs24h: { success: number; fail: number; pending: number };
      topFailures: Array<{ pipelineId: string; pipelineName: string | null; count: number; lastFailureAt: string | null }>;
    }>("/api/overview"),

  // run activities
  getRunActivities: (id: string) =>
    request<{
      activities: Array<{
        type: string;
        scheduledAt?: string;
        startedAt?: string;
        completedAt?: string;
        status: "scheduled" | "started" | "success" | "fail";
        failure?: { message: string; stackTrace?: string } | null;
      }>;
      workflowId?: string;
      workflowRunId?: string;
      error?: string;
    }>(`/api/runs/${encodeURIComponent(id)}/activities`),

  testConnection: (id: string) =>
    request<{ ok: boolean; detail?: string }>(
      `/api/connector-instances/${encodeURIComponent(id)}/test`,
      { method: "POST" },
    ),

  // Re-run a connector's idempotent setup (e.g. HubSpot creates any missing
  // jump_* custom fields). Safe to call repeatedly. (DEV-4729)
  runSetup: (id: string) =>
    request<{ ok: boolean; detail?: string }>(
      `/api/connector-instances/${encodeURIComponent(id)}/setup`,
      { method: "POST" },
    ),
};
